import { useContext } from 'react'
import * as Sentry from '@sentry/browser'
import { useLocation } from 'react-router-dom'
import { AppContext } from 'contexts/AppProvider'
import { useAppDispatch } from 'store'
import { useSelector } from 'react-redux'
import {
  buffetSelector,
  resetBuffetModal,
  resetBuffetScreen,
  setBuffetEndTime,
  setCurrentOrder,
  setAllowedSharingCoupon,
  setTotalUsersSharedCoupons,
} from 'store/slices/buffetSlice'
import { BuffetOrderQuery, OrderStatusEnum, useBuffetOrderLazyQuery } from 'graphQL/dinein/generate/operations'
import useLoadingPage from 'hooks/useLoadingPage'
import { dineInClient } from 'variables/services'
import useValidateCurrentOrder from 'hooks/buffet/useValidateCurrentOrder'
import useActionParamShareCoupon from 'hooks/buffet/useActionParamShareCoupon'
import { countTotalUserSharedCoupons, isCurrentUserAllowedSharingCoupon } from 'helpers/coupon'
import { ModalEnum } from 'components/buffet/BuffetModals'
import { BuffetPath } from 'pages/buffet/BuffetPages'
import { ScreenEnum } from 'components/buffet/BuffetOverlayPage'
import { isDevelopment } from 'variables/environment'

const useBuffetOrderManagement = () => {
  const { currentUser, brand } = useContext(AppContext)
  const buffet = useSelector(buffetSelector)
  const dispatch = useAppDispatch()
  const { isLoadingPage, setIsLoadingPage } = useLoadingPage({ initialIsLoadingPage: true })
  const { pathname } = useLocation()
  const { handleGlobalCartValidation, handleCheckoutValidation, handlePromotionValidation, handleDefaultValidation } =
    useValidateCurrentOrder()
  const { handleActionParam, hasActionShareCoupon, modalNoneMembership, modalLimitSharingCoupons, modalEmptyCoupons } =
    useActionParamShareCoupon()

  const [buffetOrderLazyQuery] = useBuffetOrderLazyQuery({
    client: dineInClient,
    fetchPolicy: 'no-cache',
    onCompleted: async ({ currentOrder }) => {
      try {
        dispatch(setCurrentOrder(currentOrder))
        const _allowedSharingCoupon = isCurrentUserAllowedSharingCoupon(currentUser.id, currentOrder.tableGuests)
        dispatch(setAllowedSharingCoupon(_allowedSharingCoupon))
        const totalUserSharedCoupons = countTotalUserSharedCoupons(currentOrder.tableGuests)
        dispatch(setTotalUsersSharedCoupons(totalUserSharedCoupons))

        if (buffet.endTime === null || buffet.endTime !== currentOrder.buffetEndTime) {
          dispatch(setBuffetEndTime(currentOrder.buffetEndTime))
        }

        if (hasActionShareCoupon) {
          await handleActionParam(_allowedSharingCoupon)
          return
        }

        if (customValidateOrder(currentOrder)) {
          dispatch(resetBuffetModal())
          dispatch(resetBuffetScreen())
        }
      } catch (error) {
        !isDevelopment &&
          Sentry.captureException(new Error(`Buffet: Error loading current order: ${JSON.stringify(error)}`))
      } finally {
        setIsLoadingPage(false)
      }
    },
  })

  const customValidateOrder = (order: BuffetOrderQuery['currentOrder']) => {
    if (order.status === OrderStatusEnum.ReadyToCheckout && buffet.screen.type === ScreenEnum.StaffInitiateCheckout) {
      dispatch(resetBuffetScreen())
    }
    if (order.staffCallingReason === null && buffet.modal.type === ModalEnum.CallingStaff) {
      dispatch(resetBuffetModal())
    }
    switch (pathname) {
      case BuffetPath.GlobalCart:
        return handleGlobalCartValidation(order).isOK
      case BuffetPath.Checkout:
        return handleCheckoutValidation(order).isOK
      case BuffetPath.Promotions:
        return handlePromotionValidation(order).isOK
      default:
        return handleDefaultValidation(order)
    }
  }

  const refreshBuffetOrder = async () => {
    return await buffetOrderLazyQuery({
      variables: {
        tableID: buffet.table.tableID,
        orderID: buffet.table.orderID,
        vendorID: Number(brand?.id),
      },
    })
  }

  return {
    isLoadingPage,
    buffetOrderLazyQuery,
    refreshBuffetOrder,
    modalNoneMembership,
    modalLimitSharingCoupons,
    modalEmptyCoupons,
  }
}

export default useBuffetOrderManagement
